import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import { RemoveRedEye } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import useFetchSection from "hooks/useFetchSection";
import DeleteModal from "components/UI/Form/partials/Delete";
import { InputTypes } from "constants/enums";
import { IFormField } from "models/app";
import AddModalReseller from "./partials/Add";
import EditModalReseller from "./partials/Edit";
import { IGoal, IGoalInput } from "models/goal";
import { addQuestion, deleteQuestion, editQuestion, getQuestions, QuestionCells } from "framework/question";
import Image from "components/UI/Image";
import { addGoal, deleteGoal, editGoal, getGoals, GoalCells } from "framework/goal";

interface Props {
  feature: Feature;
}


const Goals: React.FC<Props> = ({ feature }) => {

  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterPolicies, setFilterPolicies] = useState<IGoal[]>([] as IGoal[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [SelectedPolicy, setSelectedPolicy] = useState<IGoal | null>(null);
  const [inputObj, setInputObj] = useState<IGoalInput>({} as IGoalInput);
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addUserMutation = useMutation({
    mutationFn: (createInput: FormData) => {
      return addGoal(createInput);
    },
  });

  const editUserMutation = useMutation({
    mutationFn: (data: { createInput: FormData; resellerId: string }) => {
      return editGoal(data.createInput, data.resellerId);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteGoal(id);
    },
  });

  const {
    loading,
    data: Goals,
    error,
    revalidate,
  } = useFetchSection({
    fetchData: () => getGoals(),
    fetchPermission: async () => null,
    section: "Goals",
    onSuccess: (data) => setFilterPolicies(data),
  });


  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => {
    setOpenAdd(true);
    setInputObj((prev) => ({
      ...prev
    }));
  }

  const handleOpenEdit = (user: IGoal) => {
    setSelectedPolicy(user);
    setOpenEdit(true);
  };

  const handleOpenDelete = (user: IGoal) => {
    setSelectedPolicy(user);
    setOpenDelete(true);
  };

  const handleClose = () => {
    setOpenAdd(false)
    setOpenEdit(false);
    setOpenDelete(false);
    setSelectedPolicy(null);
    setInputObj({} as IGoalInput)
  }

  const getBodyCells = (row: IGoal) => {
    let cells = Object.keys(row).map((key: string, i: number) => {

      if (key === "icon")
        return {
          id: "icon",
          element: (
            <TableCell sx={{
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#f5f5f5', // Light gray background
            }} align={"left"} key={i}>
              <Image url={row?.icon} alt="image" />
            </TableCell>
          ),
        };

      if (key === "goalQuestion")
        return {
          id: "goalQuestion",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.goalQuestion}</Typography>
            </TableCell>
          ),
        };


      if (key === "goalID")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
              >

                <Tooltip title={"Edit"}>
                  <IconButton
                    size={"small"}
                    color={"primary"}
                    onClick={() => handleOpenEdit(row)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>


                <Tooltip title={"Delete"}>
                  <IconButton
                    size={"small"}
                    sx={{
                      color: "red",
                    }}
                    onClick={() => handleOpenDelete(row)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {


    // if (
    //   !isValidNumber(inputObj.countryCode + inputObj.contactNumber))


    //   return setAlert({
    //     open: true,
    //     message: "This Phone is not valid in this country",
    //     type: "error",
    //   });

    try {
      setLoading2(true);

      let formData = new FormData();

      formData.append("goalQuestion", inputObj.goalQuestion);
      formData.append("icon", inputObj.icon);

      await addUserMutation.mutateAsync(formData);
      setAlert({
        open: true,
        message: `Goal added successfully`,
        type: "success",
      });
      revalidate();
      handleClose();
      setInputObj({} as IGoalInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {


    if (inputObj.goalQuestion === null || inputObj.icon === null)
      return setAlert({
        open: true,
        message: "Fill all required fields",
        type: "error",
      });

    try {
      setLoading2(true);


      let formData = new FormData();

      inputObj.goalQuestion && formData.append("goalQuestion", inputObj.goalQuestion);
      inputObj.icon && formData.append("icon", inputObj.icon);

      await editUserMutation.mutateAsync({
        createInput: formData,
        resellerId: SelectedPolicy!?.goalID?.toString()
      });



      setAlert({
        open: true,
        message: `Question updated successfully`,
        type: "success",
      });
      revalidate();
      handleClose();
      setInputObj({} as IGoalInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteUserMutation.mutateAsync(SelectedPolicy!.goalID?.toString());
      setAlert({
        open: true,
        message: `Goal deleted successfully`,
        type: "success",
      });
      revalidate();
      handleClose();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = (value: string) => {
    if (!Goals) return;

    const searchTerm = value.trim().toLowerCase();

    if (!searchTerm) {
      setFilterPolicies(Goals);
      return;
    }

    const filtered = Goals.filter(({ goalQuestion }) => {
      const fullText = `${goalQuestion}`.toLowerCase();
      return fullText.includes(searchTerm);
    });

    setFilterPolicies(filtered);
  };


  let addBasicFields: IFormField[] = [
    {
      name: 'goalQuestion',
      label: 'Goal Question',
      type: InputTypes.TEXT,
      placeholder: 'Type Goal Question',
      internalLabel: true,
      required: true
    },
    {
      name: 'icon',
      label: 'Image',
      type: InputTypes.FILE,
      placeholder: 'Select Image',
      internalLabel: true,
      required: true
    }
  ];


  let editBasicFields: IFormField[] = [
    {
      name: 'goalQuestion',
      label: 'Goal Question',
      type: InputTypes.TEXT,
      placeholder: 'Type Goal Question',
      defaultValue: SelectedPolicy?.goalQuestion || '',
      internalLabel: true,
      required: true
    },
    {
      name: 'icon',
      label: 'Image',
      type: InputTypes.FILE,
      placeholder: 'Select File',
      defaultValue: SelectedPolicy?.icon || '',
      internalLabel: true,
      required: true
    }
  ];


  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"

        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
            sx={{ mb: 4 }}
          >
            <Typography
              variant="h3"
              color={"header.primary"}
            >
              {(feature.name)}
            </Typography>

            <Tooltip title={"Add"}>
              <IconButton
                sx={{
                  color: "black",
                  height: 40,
                  width: 40,
                  transition: "0.5s",
                }}
                onClick={handleOpenAdd}
              >
                <Add />
              </IconButton>
            </Tooltip>

          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={1}
              width={"100%"}
            >
              <Search handleChange={handleSearch} placeholder={"Search"} />
            </Stack>
          </Stack>
        </Grid>

        {FilterPolicies.length > 0 && (
          <List
            headCells={GoalCells}
            data={FilterPolicies}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterPolicies || FilterPolicies.length === 0) &&

          <EmptyContent
            icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
            header={"No Goals"}
            message={`No Goal Exists Yet`}
          />
        }


      </Stack>

      {openAdd && (
        <AddModalReseller
          open={openAdd}
          loading={loading2}
          disabled={!inputObj.goalQuestion || !inputObj.icon}
          title={`Add Goal`}
          basicFields={addBasicFields}
          onClose={handleClose}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditModalReseller
          open={openEdit}
          loading={loading2}
          title={`Edit Goal`}
          basicFields={editBasicFields}
          onClose={handleClose}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteModal
          open={openDelete}
          loading={loading2}
          title={`Delete Goal`}
          description={`Are You Sure You Want to delete this Goal`}
          onClose={handleClose}
          onSubmit={handleDelete}
        />
      )}
      {/* {openView && (
        <ViewClientsModal
          open={openView}
          title="View Client Subscriptions"
          feature={feature}
          onClose={handleClose}
        />
      )} */}
    </>
  );
};

export default Goals;
